import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Hash, Inbox, MessageSquare, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Popover } from '../../../components/ui/popover';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../../components/ui/avatar';
import {
  AppBarBadgeButton,
  AppBarPopoverList,
  AppBarPopoverItem,
} from '../../../components/layout/appBarPopover';
import { useMessagingUnreadSummary } from '../hooks/useMessagingApi';
import { useRelativeDate } from '../../../shared/hooks';
import type { UnreadConversationPreview } from '../../../generated/api/models/unreadConversationPreview';

interface MessagingInboxPopoverProps {
  moduleColor: string;
  /**
   * Rendering variant (same fold pattern as AppBarMailFilesShortcuts /
   * AnnouncementsBell):
   * - `inline` (default): AppBar badge button + inbox preview popover (desktop).
   * - `menu`: a flat row for the mobile header overflow menu — navigates
   *   straight to `/messaging` (the vertical menu has no room for a nested
   *   popover), with the unread count as a trailing badge.
   */
  variant?: 'inline' | 'menu';
}

function ConversationAvatar({ row }: { row: UnreadConversationPreview }) {
  if (row.conversation_type === 'dm') {
    const initial = row.title?.charAt(0)?.toUpperCase() || '?';
    return (
      <Avatar className="h-7 w-7">
        {row.avatar_url && <AvatarImage src={row.avatar_url} alt={row.title} />}
        <AvatarFallback className="text-xs">{initial}</AvatarFallback>
      </Avatar>
    );
  }
  const Icon =
    row.conversation_type === 'public_channel' ||
    row.conversation_type === 'private_channel'
      ? Hash
      : Users;
  return (
    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
    </div>
  );
}

export function MessagingInboxPopover({ moduleColor, variant = 'inline' }: MessagingInboxPopoverProps) {
  const { t } = useTranslation(['messaging', 'common']);
  const navigate = useNavigate();
  const formatRelative = useRelativeDate();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useMessagingUnreadSummary(5);
  const total = data?.total_unread ?? 0;
  const recent = data?.recent_unread ?? [];

  const handleSelect = (conversationId: string) => {
    setOpen(false);
    navigate(`/messaging?conversation=${conversationId}`);
  };

  const handleOpenAll = () => {
    setOpen(false);
    navigate('/messaging');
  };

  // Mobile overflow: flat menu row → messaging inbox, unread as a trailing badge.
  if (variant === 'menu') {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start gap-2 font-normal text-foreground"
        onClick={handleOpenAll}
      >
        <MessageSquare className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-start">{t('messaging:inboxPopover.title')}</span>
        {total > 0 && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <AppBarBadgeButton
        icon={<MessageSquare className="h-4 w-4" />}
        count={total}
        ariaLabel={t('common:notifications.messagingAriaLabel')}
        tooltip={
          total > 0
            ? t('common:thread.unreadMessages', { count: total })
            : t('common:notifications.messagingTooltip')
        }
        moduleColor={moduleColor}
      />
      <AppBarPopoverList
        title={t('messaging:inboxPopover.title')}
        headerAction={
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground"
            onClick={handleOpenAll}
          >
            {t('messaging:inboxPopover.openAll')}
          </Button>
        }
        isLoading={isLoading}
        isEmpty={recent.length === 0}
        emptyIcon={<Inbox className="h-8 w-8" />}
        emptyMessage={t('messaging:inboxPopover.empty')}
      >
        {recent.map((row) => {
          const senderPrefix = row.last_message_sender_name
            ? `${row.last_message_sender_name}: `
            : '';
          return (
            <AppBarPopoverItem
              key={row.conversation_id}
              onClick={() => handleSelect(row.conversation_id)}
              unread={row.unread_count > 0}
              leading={<ConversationAvatar row={row} />}
              title={row.title || t('messaging:inboxPopover.untitled')}
              subtitle={`${senderPrefix}${row.last_message_preview}`}
              meta={formatRelative(new Date(row.last_message_at))}
              trailing={
                row.unread_count > 0 ? (
                  <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                    {row.unread_count > 99 ? '99+' : row.unread_count}
                  </span>
                ) : undefined
              }
            />
          );
        })}
      </AppBarPopoverList>
    </Popover>
  );
}
